import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FerrisWheel, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CustomerInfoForm } from '@/components/pos/CustomerInfoForm';
import { CouponField } from '@/components/pos/CouponField';
import { DiscountList } from '@/components/pos/DiscountList';
import { MoneyValue } from '@/components/pos/MoneyValue';
import { StepIndicator } from '@/components/pos/StepIndicator';
import { PaymentDialog } from '@/components/pos/PaymentDialog';
import { PaidScreen } from '@/components/pos/PaidScreen';
import { useCartStore, cartSelectors, selectTotal } from '@/stores/cart.store';
import { useShallow } from 'zustand/react/shallow';
import { useHotkeys } from '@/hooks/useHotkeys';
import { usePaymentMethods } from '@/hooks/queries';
import { getPromoCandidates } from '@/api/discount.api';
import { getSaleQuote } from '@/api/sale.api';
import { buildSalePayload } from '@/lib/buildSalePayload';
import { useQuery } from '@tanstack/react-query';
import type { CreateSaleResult } from '@/types/sale';

export function CheckoutPage() {
  const navigate = useNavigate();
  const { lines, bundleLines, customer, visitDate, couponCode, discountIds, promo } = useCartStore(
    useShallow((s) => ({
      lines: s.lines,
      bundleLines: s.bundleLines,
      customer: s.customer,
      visitDate: s.visitDate,
      couponCode: s.couponCode,
      discountIds: s.discountIds,
      promo: s.promo,
    })),
  );
  const subtotal = useCartStore(cartSelectors.subtotal);
  const savings = useCartStore(cartSelectors.couponSavings);
  const localTotal = useCartStore(selectTotal);
  const cartCount = lines.length + bundleLines.length;

  // Itemize attraction children in the summary (main lines show main-only price).
  const attractionSummary = useMemo(() => {
    const map = new Map<string, { name: string; quantity: number; total: number }>();
    const fold = (a: { productId: string; name: string; price: number; quantity: number }) => {
      const cur = map.get(a.productId) ?? { name: a.name, quantity: 0, total: 0 };
      cur.quantity += a.quantity;
      cur.total += a.price * a.quantity;
      map.set(a.productId, cur);
    };
    for (const l of lines) for (const seat of l.seats) seat.addOns.forEach(fold);
    // Extra attractions attached to bundles are itemized here too.
    for (const b of bundleLines) b.extraAddOns.forEach(fold);
    return Array.from(map.values());
  }, [lines, bundleLines]);

  const [customerValid, setCustomerValid] = useState(Boolean(customer.name.trim()));
  const [payOpen, setPayOpen] = useState(false);
  const [paid, setPaid] = useState<CreateSaleResult | null>(null);

  // Empty-cart guard.
  useEffect(() => {
    if (cartCount === 0 && !paid) navigate('/', { replace: true });
  }, [cartCount, paid, navigate]);

  const eligibleCart = useMemo(
    () => ({
      items: lines.map((l) => ({
        productId: l.productId,
        quantity: l.seats.length,
        price: l.unitPrice,
      })),
      totalAmount: subtotal,
    }),
    [lines, subtotal],
  );

  // ─── Live BE-authoritative total (POST /sale/quote) ────────────────────────
  // A neutral placeholder payment method satisfies the validator; price is
  // method-independent (rate plan → slot override → event surge → discounts),
  // so the shown total equals the eventual charge. Falls back to the client
  // estimate on any error.
  const { data: methods = [] } = usePaymentMethods();
  // Prefer a non-e-money method: e-money requires a reader on billingInfo, which
  // isn't chosen until the payment dialog. Price is method-independent anyway.
  const quoteMethod = useMemo(
    () => methods.find((m) => m.paymentGateway !== 'e-money') ?? methods[0],
    [methods],
  );

  // Promo-scheme context (v.1.0.22). Scheme-gated discounts (GROUP / IDENTITAS /
  // PAYMENT / channel) only appear as eligible when the matching context is
  // supplied. `idCardNumber` is deliberately omitted — it's PII and not needed
  // for eligibility. Note: PAYMENT-gated promos surface against the quote's
  // placeholder method, so the shown gateway may differ from the one actually
  // chosen at the payment dialog.
  const eligibleContext = useMemo(
    () => ({
      groupType: promo.groupType,
      sourceChannel: promo.sourceChannel,
      paymentGateway: quoteMethod?.paymentGateway,
      paymentMethodId: quoteMethod?.id,
      identity: promo.identity
        ? {
            idCardRegion: promo.identity.idCardRegion ?? undefined,
            birthMonth: promo.identity.birthMonth,
            ktpVerified: promo.identity.ktpVerified,
          }
        : null,
      // Document-OCR scan → the preview endpoint runs the same document-gate
      // sub-validator, so a scanned-document promo surfaces as eligible here.
      // The authoritative gate is still the sale-time `documentScanToken`.
      document: promo.documentScan
        ? { documentType: promo.documentScan.documentType, fields: promo.documentScan.fields }
        : null,
    }),
    [promo, quoteMethod],
  );

  const { data: candidates = [] } = useQuery({
    queryKey: ['promoCandidates', eligibleCart.totalAmount, cartCount, eligibleContext],
    queryFn: () => getPromoCandidates(eligibleCart, eligibleContext),
    enabled: cartCount > 0 && !paid,
  });
  const quotePayload = useMemo(() => {
    if (!quoteMethod || cartCount === 0) return null;
    return buildSalePayload({
      lines,
      bundleLines,
      customer: { ...customer, name: customer.name.trim() || 'Guest' },
      visitDate,
      couponCode,
      discountIds,
      paymentMethodId: quoteMethod.id,
      paymentGateway: quoteMethod.paymentGateway,
      // Placeholder reader only when the sole method is e-money (validator needs it).
      reader:
        quoteMethod.paymentGateway === 'e-money' ? { mid: '00000', tid: '00000' } : undefined,
      groupType: promo.groupType,
      sourceChannel: promo.sourceChannel,
      identity: promo.identity,
      documentScanToken: promo.documentScanToken,
    });
  }, [
    quoteMethod,
    cartCount,
    lines,
    bundleLines,
    customer,
    visitDate,
    couponCode,
    discountIds,
    promo,
  ]);

  const quoteQuery = useQuery({
    queryKey: ['saleQuote', quotePayload],
    queryFn: () => getSaleQuote(quotePayload!),
    enabled: Boolean(quotePayload) && !paid,
    staleTime: 10_000,
    retry: false,
  });

  const total = quoteQuery.data?.total ?? localTotal;
  const discount = quoteQuery.data ? quoteQuery.data.totalDiscount : savings;

  const canPay = customerValid && cartCount > 0;

  useHotkeys({
    f8: () => canPay && setPayOpen(true),
    f9: () => canPay && setPayOpen(true),
    escape: () => !payOpen && !paid && navigate('/details'),
  });

  if (paid) {
    return <PaidScreen result={paid} />;
  }

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col px-6 py-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <Button variant="ghost" className="gap-2" onClick={() => navigate('/details')}>
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Button>
        <StepIndicator current={2} />
      </div>

      <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-5">
        {/* Left — forms. `min-h-0` lets this grid item shrink below its content
            so the inner ScrollArea gets a bounded height and actually scrolls. */}
        <div className="min-h-0 lg:col-span-3">
          <ScrollArea className="h-full pr-3">
            <div className="space-y-5 pb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Informasi pelanggan</CardTitle>
                </CardHeader>
                <CardContent>
                  <CustomerInfoForm onValidityChange={setCustomerValid} />
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Kupon & promo</CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  <CouponField />
                  <DiscountList candidates={candidates} />
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        </div>

        {/* Right — summary */}
        <div className="min-h-0 lg:col-span-2">
          <Card className="flex h-full flex-col">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" /> Ringkasan pesanan
              </CardTitle>
            </CardHeader>
            <CardContent className="flex min-h-0 flex-1 flex-col">
              <ScrollArea className="flex-1">
                <ul className="space-y-2 pr-2">
                  {bundleLines.map((b) => (
                    <li key={b.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">
                          📦 {b.name}
                        </span>
                        <span className="tnum text-xs text-ink-400">paket · × {b.quantity}</span>
                      </span>
                      <MoneyValue value={b.unitPrice * b.quantity} className="text-sm" />
                    </li>
                  ))}
                  {lines.map((l) => (
                    <li key={l.key} className="flex justify-between gap-2 text-sm">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink-900">{l.name}</span>
                        <span className="tnum text-xs text-ink-400">× {l.seats.length}</span>
                      </span>
                      <MoneyValue value={l.unitPrice * l.seats.length} className="text-sm" />
                    </li>
                  ))}
                  {attractionSummary.map((a) => (
                    <li key={a.name} className="flex justify-between gap-2 text-sm">
                      <span className="flex min-w-0 items-center gap-1.5">
                        <FerrisWheel className="h-3.5 w-3.5 shrink-0 text-gold-600" />
                        <span className="min-w-0">
                          <span className="block truncate text-ink-700">{a.name}</span>
                          <span className="tnum text-xs text-ink-400">× {a.quantity}</span>
                        </span>
                      </span>
                      <MoneyValue value={a.total} className="text-sm" />
                    </li>
                  ))}
                </ul>
              </ScrollArea>

              <div className="mt-4 space-y-1.5 border-t border-border pt-4 text-sm">
                <div className="flex justify-between text-ink-600">
                  <span>Subtotal</span>
                  <MoneyValue value={subtotal} />
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-success">
                    <span>Diskon</span>
                    <span>− <MoneyValue value={discount} className="text-success" /></span>
                  </div>
                )}
                <Separator className="my-2" />
                <div className="flex items-end justify-between">
                  <span className="font-semibold uppercase tracking-wide text-ink-600">Total</span>
                  <span className="text-right">
                    <MoneyValue value={total} emphasis className="text-3xl" />
                    {quoteQuery.isFetching && (
                      <span className="block text-[11px] font-normal text-ink-400">menghitung…</span>
                    )}
                  </span>
                </div>
              </div>

              <Button
                size="lg"
                variant="gold"
                className="mt-4 h-16 w-full text-xl"
                disabled={!canPay}
                onClick={() => setPayOpen(true)}
              >
                Bayar <ArrowRight className="h-6 w-6" />
                <kbd className="ml-1 rounded border border-ink-900/20 px-1.5 text-xs">F8</kbd>
              </Button>
              {!customerValid ? (
                <p className="mt-2 text-center text-xs text-warning">
                  Lengkapi nama pelanggan untuk melanjutkan.
                </p>
              ) : null}
            </CardContent>
          </Card>
        </div>
      </div>

      <PaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        total={total}
        onPaid={(result) => {
          setPayOpen(false);
          setPaid(result);
        }}
        onCapacityBlocked={() => navigate('/')}
      />
    </div>
  );
}
